import React, { useMemo, useRef, useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { ChevronDown, X } from "lucide-react";

/**
 * Combobox autocomplete — pour saisir une marque ou un modèle avec suggestions.
 * - L'utilisateur peut TOUJOURS taper une valeur libre (pas restreint à la liste).
 * - Les suggestions s'affichent au focus et se filtrent à la frappe.
 *
 * Props :
 *  value: string
 *  onChange: (val: string) => void
 *  options: string[]
 *  placeholder?: string
 *  testId?: string
 *  className?: string
 *  disabled?: boolean
 */
export default function Combobox({ value, onChange, options = [], placeholder, testId, className, disabled }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const ref = useRef(null);

  const filtered = useMemo(() => {
    const v = (value || "").trim().toLowerCase();
    if (!v) return options.slice(0, 60);
    return options.filter((o) => o.toLowerCase().includes(v)).slice(0, 60);
  }, [value, options]);

  // Close on outside click
  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (val) => {
    onChange(val);
    setOpen(false);
  };

  return (
    <div ref={ref} className={`relative ${className || ""}`} data-testid={testId}>
      <Input
        value={value || ""}
        disabled={disabled}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(filtered.length - 1, h + 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
          else if (e.key === "Enter" && filtered[highlight]) { e.preventDefault(); pick(filtered[highlight]); }
          else if (e.key === "Escape") { setOpen(false); }
        }}
        placeholder={placeholder}
        className="rounded-sm pr-9"
      />
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        tabIndex={-1}
      >
        {value ? (
          <X
            size={14}
            onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
            className="hover:text-destructive"
          />
        ) : (
          <ChevronDown size={14} />
        )}
      </button>

      {open && filtered.length > 0 && (
        <div
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto border border-border bg-popover text-popover-foreground rounded-sm shadow-lg"
          data-testid={testId ? `${testId}-list` : undefined}
        >
          {filtered.map((opt, i) => (
            <button
              key={opt}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(opt); }}
              onMouseEnter={() => setHighlight(i)}
              className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 ${i === highlight ? "bg-slate-100" : ""}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
